/**
 * 客服消息主页面
 *
 * 左右分栏布局：
 * - 左侧：活跃用户列表（搜索、分页、SSE 实时更新）
 * - 右侧：对话面板（消息历史、回复输入）
 */

import { useState, useCallback, useRef } from 'react';
import { ActiveUsersList } from '../components/ActiveUsersList';
import { ConversationPanel } from '../components/ConversationPanel';
import { useWechatSse, type SseNewMessage } from '../hooks/useWechatSse';
import { markAsRead } from '../hooks/useWechatApi';
import type { ActiveUser } from '../hooks/useWechatApi';

export default function CustomerServicePage() {
  const [selectedUser, setSelectedUser] = useState<ActiveUser | null>(null);
  // openid → 是否有未读消息
  const [unreadMap, setUnreadMap] = useState<Record<string, boolean>>({});
  // 用于通知 ConversationPanel 有新 SSE 消息
  const [sseIncomingMessage, setSseIncomingMessage] = useState<SseNewMessage | null>(null);
  // 活跃用户列表刷新触发器
  const refreshActiveUsersRef = useRef<(() => void) | null>(null);

  // SSE 新消息处理
  const handleSseNewMessage = useCallback(
    (message: SseNewMessage) => {
      // 更新未读映射
      if (selectedUser && message.openid === selectedUser.openid) {
        // 当前选中用户的消息 → 自动追加到对话 + 标记已读
        setSseIncomingMessage(message);
        void markAsRead(message.openid);
      } else {
        // 其他用户的消息 → 标记未读红点
        setUnreadMap((prev) => ({ ...prev, [message.openid]: true }));
      }
      // 刷新活跃用户列表（可能有新用户进入或排序变化）
      refreshActiveUsersRef.current?.();
    },
    [selectedUser]
  );

  const { status: sseStatus } = useWechatSse({
    onNewMessage: handleSseNewMessage,
    enabled: true,
  });

  // 选中用户
  const handleSelectUser = useCallback(
    async (user: ActiveUser) => {
      setSelectedUser(user);
      // 清除该用户的未读标记
      setUnreadMap((prev) => {
        const next = { ...prev };
        delete next[user.openid];
        return next;
      });
      // 标记该用户消息为已读
      try {
        await markAsRead(user.openid);
      } catch {
        // 静默处理
      }
    },
    []
  );

  return (
    <div className="h-full flex flex-col">
      {/* 页面标题栏 */}
      <div className="h-14 px-6 flex items-center justify-between border-b border-divider shrink-0">
        <h1 className="text-lg font-semibold">客服消息</h1>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span
            className={`inline-block w-2 h-2 rounded-full ${
              sseStatus === 'connected'
                ? 'bg-green-500'
                : sseStatus === 'connecting'
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
            }`}
          />
          {sseStatus === 'connected' ? '实时连接' : sseStatus === 'connecting' ? '连接中...' : '连接断开'}
        </div>
      </div>

      {/* 主内容区 */}
      <div className="flex-1 flex min-h-0">
        {/* 左侧：活跃用户列表 */}
        <div className="w-80 border-r border-divider flex flex-col shrink-0">
          <ActiveUsersList
            selectedOpenid={selectedUser?.openid ?? null}
            unreadMap={unreadMap}
            onSelectUser={handleSelectUser}
            onRefreshRef={refreshActiveUsersRef}
          />
        </div>

        {/* 右侧：对话面板 */}
        <div className="flex-1 min-w-0 flex flex-col">
          <ConversationPanel
            selectedUser={selectedUser}
            sseIncomingMessage={sseIncomingMessage}
            onSseMessageConsumed={() => setSseIncomingMessage(null)}
          />
        </div>
      </div>
    </div>
  );
}
